import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  updateDriver,
  clearError,
  fetchBaseLocations,
} from "../features/driver/driverSlice";
import { Spinner, Alert } from "react-bootstrap";
import { contactPersonIcon, contactNumberIcon, emailIcon } from "../assets";
import CustomAlert from "../components/CustomAlert";
import "../css/EditDriver.css";

const EditDriver = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [baseDropdownOpen, setBaseDropdownOpen] = useState(false);

  // Custom Alert State
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: "success",
    title: "",
    message: "",
  });

  // Custom Alert Helper
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  // Get loading and error state from Redux
  const { isLoading, error, baseLocations, baseLocationsLoading } = useSelector(
    (state) => state.driver,
  );

  // Get driver data from location state
  const driverData = location.state?.driver || {};

  // Extract driver ID - try multiple possible keys
  const driverId =
    driverData.driverId ||
    driverData.id ||
    driverData.ID ||
    driverData.DriverId;

  console.log("Driver Data:", JSON.stringify(driverData, null, 2));

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNo: "",
    email: "",
    baseLocationIds: [],
  });

  useEffect(() => {
    dispatch(fetchBaseLocations());
  }, [dispatch]);

  // Pre-fill form with driver data
  useEffect(() => {
    if (driverData) {
      setFormData({
        firstName: driverData.firstName || driverData.FirstName || "",
        lastName: driverData.lastName || driverData.LastName || "",
        mobileNo: driverData.mobileNo || driverData.Phone || "",
        email: driverData.email || driverData.Email || "",
        baseLocationIds: (driverData.assignedBases || []).map((b) => b.BaseId),
      });
    }
  }, [driverData, driverId]);

  const handleBaseToggle = (baseId) => {
    setFormData((prev) => ({
      ...prev,
      baseLocationIds: prev.baseLocationIds.includes(baseId)
        ? prev.baseLocationIds.filter((id) => id !== baseId)
        : [...prev.baseLocationIds, baseId],
    }));
  };

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Last name is required";
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.mobileNo.trim()) {
      errors.mobileNo = "Mobile number is required";
    } else if (!/^\+?[\d\s()-]{10,}$/.test(formData.mobileNo)) {
      errors.mobileNo = "Invalid mobile number format";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email format";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showCustomAlert(
        "error",
        "Validation Error",
        "Please fix all errors before submitting",
      );
      return;
    }

    // Check if driverId exists
    if (!driverId) {
      showCustomAlert(
        "error",
        "Missing ID",
        "Driver ID not found. Please go back and try again.",
      );
      return;
    }

    try {
      // Prepare data for API - exactly as backend expects
      const updateData = {
        DriverId: parseInt(driverId),
        FirstName: formData.firstName.trim(),
        LastName: formData.lastName.trim(),
        Phone: formData.mobileNo.trim(),
        Email: formData.email.trim(),
        BaseLocationIds: formData.baseLocationIds,
      };

      // Call API through Redux - Pass data directly
      await dispatch(updateDriver(updateData)).unwrap();

      // Show success modal
      setShowSuccessModal(true);
    } catch (err) {
      // Show user-friendly error message
      const errorMessage =
        typeof err === "string"
          ? err
          : err?.message || "Failed to update driver. Please try again.";

      showCustomAlert("error", "Update Failed", errorMessage);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowSuccessModal(false);
    navigate("/driver", { replace: true });
  };

  // Handle back button
  const handleBack = () => {
    navigate("/driver", { replace: true });
  };

  return (
    <div className="edit-driver-page">
      <div className="edit-container">
        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => dispatch(clearError())}
          >
            <strong>Error:</strong> {error}
          </Alert>
        )}

        <div className="edit-card">
          <div className="edit-header">
            <h1 className="edit-title">Edit Driver</h1>
            <p className="edit-description">
              Update driver or team member information. Changes will be saved to
              your portal
            </p>
          </div>

          <form onSubmit={handleSubmit} className="edit-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  First Name <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <img src={contactPersonIcon} alt="" className="input-icon" />
                  <input
                    type="text"
                    name="firstName"
                    className={`form-control ${formErrors.firstName ? "error" : ""}`}
                    placeholder="Enter first name"
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                {formErrors.firstName && (
                  <span className="error-message">{formErrors.firstName}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Last Name <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <img src={contactPersonIcon} alt="" className="input-icon" />
                  <input
                    type="text"
                    name="lastName"
                    className={`form-control ${formErrors.lastName ? "error" : ""}`}
                    placeholder="Enter last name"
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                {formErrors.lastName && (
                  <span className="error-message">{formErrors.lastName}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">
                  Mobile No <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <img src={contactNumberIcon} alt="" className="input-icon" />
                  <input
                    type="tel"
                    name="mobileNo"
                    className={`form-control ${formErrors.mobileNo ? "error" : ""}`}
                    placeholder="Enter mobile number"
                    value={formData.mobileNo}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 10);
                      setFormData((prev) => ({ ...prev, mobileNo: value }));
                    }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    maxLength={10}
                    disabled={isLoading}
                  />
                </div>
                {formErrors.mobileNo && (
                  <span className="error-message">{formErrors.mobileNo}</span>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">
                  Email-id <span className="required">*</span>
                </label>
                <div className="input-wrapper">
                  <img src={emailIcon} alt="" className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    className={`form-control ${formErrors.email ? "error" : ""}`}
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={isLoading}
                  />
                </div>
                {formErrors.email && (
                  <span className="error-message">{formErrors.email}</span>
                )}
              </div>

            
            </div>
              <div className="form-group full-width">
                <label className="form-label">Assign Base</label>
                <div className="base-dropdown-wrapper">
                  <div
                    className={`base-dropdown-trigger ${baseDropdownOpen ? "open" : ""}`}
                    onClick={() => setBaseDropdownOpen((prev) => !prev)}
                  >
                    <svg
                      className="base-trigger-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                    <span
                      className={`base-trigger-text ${formData.baseLocationIds.length > 0 ? "selected" : ""}`}
                    >
                      {formData.baseLocationIds.length > 0
                        ? baseLocations
                            .filter((base) =>
                              formData.baseLocationIds.includes(base.BaseId),
                            )
                            .map((base) => `${base.BaseName}, ${base.City}`)
                            .join(" | ")
                        : "Assign Base Location"}
                    </span>
                    <svg
                      className={`base-chevron ${baseDropdownOpen ? "rotated" : ""}`}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>

                  {baseDropdownOpen && (
                    <div className="base-dropdown-list">
                      {baseLocationsLoading ? (
                        <p className="loading-text">Loading locations...</p>
                      ) : baseLocations.length === 0 ? (
                        <p className="loading-text">No base locations found</p>
                      ) : (
                        baseLocations.map((base) => (
                          <label
                            key={base.BaseId}
                            className="base-checkbox-item"
                          >
                            <input
                              type="checkbox"
                              checked={formData.baseLocationIds.includes(
                                base.BaseId,
                              )}
                              onChange={() => handleBaseToggle(base.BaseId)}
                            />
                            <span className="base-info">
                              <span className="base-name">
                                {base.BaseName}
                                
                              </span>
                              <span className="base-address">
                                {base.City}, {base.State}
                              </span>
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            <button
              type="submit"
              className="submit-button ed_sumit_btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner animation="border " size="sm" className="me-2" />
                  UPDATING...
                </>
              ) : (
                "UPDATE DRIVER"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="success-modal">
            <button className="close-modal-btn" onClick={handleCloseModal}>
              ✖
            </button>

            <div className="success-icon">
              <div className="check-circle">
                <span className="check-mark">✓</span>
              </div>
            </div>

            <h2 className="success-title">Driver Updated Successfully</h2>
            <p className="success-message">
              Driver information has been updated successfully. Click close to
              return to drivers page.
            </p>

            <button className="modal-btn-close" onClick={handleCloseModal}>
              CLOSE
            </button>
          </div>
        </div>
      )}

      {/* Custom Alert */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default EditDriver;
